// Soft-paywall helpers — map backend entitlement features 1:1 to analytics reasons.
// Never invent a second scheme. Accounts are never locked on SOFT_PAYWALL.

/** @type {Readonly<Record<string, 'ocr_limit'|'splitsmart_limit'|'ai_premium'>>} */
export const FEATURE_TO_ANALYTICS_REASON = Object.freeze({
  'ocr.scan': 'ocr_limit',
  'splitsmart.active': 'splitsmart_limit',
  'ai.coach': 'ai_premium',
});

/** Inverse map for copy keys */
export const ANALYTICS_REASON_TO_FEATURE = Object.freeze({
  ocr_limit: 'ocr.scan',
  splitsmart_limit: 'splitsmart.active',
  ai_premium: 'ai.coach',
});

export const SOFT_PAYWALL_EVENT = 'soft-paywall';
export const SOFT_PAYWALL_CODE = 'SOFT_PAYWALL';

/**
 * Map feature / paywall payload → analytics reason key.
 * @param {string|{feature?: string}|null|undefined} featureOrPaywall
 * @returns {'ocr_limit'|'splitsmart_limit'|'ai_premium'|null}
 */
export function analyticsReasonFromFeature(featureOrPaywall) {
  if (!featureOrPaywall) return null;
  if (typeof featureOrPaywall === 'string') {
    if (FEATURE_TO_ANALYTICS_REASON[featureOrPaywall]) {
      return FEATURE_TO_ANALYTICS_REASON[featureOrPaywall];
    }
    // Already an analytics key?
    if (ANALYTICS_REASON_TO_FEATURE[featureOrPaywall]) return featureOrPaywall;
    return null;
  }
  const feature = featureOrPaywall.feature || featureOrPaywall.paywall?.feature;
  return analyticsReasonFromFeature(feature);
}

/**
 * Normalize API body / axios error into a soft-paywall payload, or null.
 */
export function extractSoftPaywall(source) {
  if (!source) return null;

  // Axios error
  const status = source.response?.status ?? source.status;
  const data = source.response?.data ?? source.data ?? source;

  if (status != null && status !== 402) {
    // Still allow direct payload objects without status
    if (!(data?.code === SOFT_PAYWALL_CODE || data?.paywall?.code === SOFT_PAYWALL_CODE)) {
      return null;
    }
  }

  if (!data || typeof data !== 'object') return null;
  if (data.code !== SOFT_PAYWALL_CODE && data.paywall?.code !== SOFT_PAYWALL_CODE) {
    return null;
  }

  const paywall = data.paywall && typeof data.paywall === 'object' ? data.paywall : {};
  const feature = data.feature || paywall.feature || null;
  const reason = analyticsReasonFromFeature(feature);

  return {
    code: SOFT_PAYWALL_CODE,
    feature,
    analyticsReason: reason,
    message: data.message || paywall.cta || null,
    plan: data.plan || paywall.currentPlan || null,
    limit: data.limit ?? paywall.limit ?? null,
    used: data.used ?? paywall.used ?? null,
    paywall: {
      code: SOFT_PAYWALL_CODE,
      feature,
      reason: paywall.reason || null,
      label: paywall.label || null,
      currentPlan: paywall.currentPlan || data.plan || 'free',
      used: data.used ?? paywall.used ?? null,
      limit: data.limit ?? paywall.limit ?? null,
      recommendedPlan: paywall.recommendedPlan || (feature === 'ai.coach' ? 'premium' : 'standard'),
      priceEur: paywall.priceEur ?? null,
      trialDays: paywall.trialDays ?? 60,
      cta: paywall.cta || null,
      upgradeUrl: paywall.upgradeUrl || '/subscription/plans',
      checkoutHint: paywall.checkoutHint || {
        defaultUpsell: 'standard',
        aiUpsell: 'premium',
        trialDays: 60,
      },
    },
  };
}

export function isSoftPaywallError(error) {
  return !!extractSoftPaywall(error);
}

/**
 * Dispatch global soft-paywall event (SoftPaywallContext listens).
 * Safe no-op outside the browser.
 */
export function dispatchSoftPaywall(payload) {
  if (typeof window === 'undefined' || !payload) return false;
  const normalized = extractSoftPaywall(payload) || payload;
  window.dispatchEvent(new CustomEvent(SOFT_PAYWALL_EVENT, { detail: normalized }));
  return true;
}

/**
 * Error subclass so fetch-based call sites can `catch` without guessing.
 */
export class SoftPaywallError extends Error {
  constructor(payload) {
    const normalized = extractSoftPaywall(payload) || payload;
    super(normalized?.message || 'Upgrade required to continue');
    this.name = 'SoftPaywallError';
    this.status = 402;
    this.code = SOFT_PAYWALL_CODE;
    this.softPaywall = normalized;
  }
}

export default {
  FEATURE_TO_ANALYTICS_REASON,
  analyticsReasonFromFeature,
  extractSoftPaywall,
  isSoftPaywallError,
  dispatchSoftPaywall,
  SoftPaywallError,
  SOFT_PAYWALL_EVENT,
  SOFT_PAYWALL_CODE,
};
